import { useState, useEffect, useRef } from "react";
import { Upload, X } from "lucide-react";

export function ImageDropzone({ file, onChange }: { file: File | null; onChange: (f: File | null) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (f && f.type.startsWith("image/")) onChange(f);
  };

  if (file && preview) {
    return (
      <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="w-16 h-16 rounded-md bg-white border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
          <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
        </div>
        <button
          type="button"
          onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ""; }}
          className="text-gray-400 hover:text-red-500 p-1"
          aria-label="Remove image"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <label
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
        dragging ? "border-[#00d9d9] bg-[#00d9d9]/5" : "border-gray-300 hover:border-[#00d9d9] hover:bg-gray-50"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />
      <div className="w-10 h-10 rounded-full bg-[#00d9d9]/10 flex items-center justify-center text-[#00bfc0]">
        <Upload size={18} />
      </div>
      <p className="text-sm text-gray-600">
        <span className="font-medium text-[#00bfc0]">Click to upload</span> or drag and drop
      </p>
      <p className="text-xs text-gray-400">PNG, JPG, SVG or WEBP</p>
    </label>
  );
}
